// Vivier & sorties — recherche dans le pool (spec §9 écran 16, version
// v1 volontairement simple: filtres rôle / motif, repêchage vers la
// demande d'origine si toujours ouverte).
import { useMemo, useState } from 'react';
import { ENTITIES } from '@pointage/shared';
import type { RecApplicationDTO, RecRequisitionDTO } from '../../api/hooks.js';
import { RecAv, RecEnt, recStageOf } from './common.js';

export function RecVivier({ apps, reqs, canEdit, busy, onOpen, onRestore }: {
  apps: RecApplicationDTO[];
  reqs: RecRequisitionDTO[];
  canEdit: boolean;
  busy: boolean;
  onOpen: (id: string) => void;
  onRestore: (id: string) => void;
}) {
  const [mode, setMode] = useState<'VIVIER' | 'REJETE'>('VIVIER');
  const [q, setQ] = useState('');
  const reqOf = useMemo(() => new Map(reqs.map((r) => [r.id, r])), [reqs]);
  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return apps
      .filter((a) => a.stage === mode)
      .filter((a) => {
        if (!needle) return true;
        const hay = [
          a.candidate.firstName, a.candidate.lastName, a.candidate.email,
          ...(a.candidate.tags ?? []), reqOf.get(a.requisitionId)?.roleLabel ?? '', a.rejectMotif ?? '',
        ].join(' ').toLowerCase();
        return hay.includes(needle);
      })
      .sort((a, b) => (b.closedAt ?? '').localeCompare(a.closedAt ?? ''));
  }, [apps, mode, q, reqOf]);
  const nViv = apps.filter((a) => a.stage === 'VIVIER').length;
  const nRej = apps.filter((a) => a.stage === 'REJETE').length;

  return (
    <div>
      <div className="rec-ctrl" style={{ marginBottom: 14 }}>
        <div className="rec-seg">
          <button className={mode === 'VIVIER' ? 'on' : ''} onClick={() => setMode('VIVIER')}>Vivier{nViv > 0 && <span className="bdg">{nViv}</span>}</button>
          <button className={mode === 'REJETE' ? 'on' : ''} onClick={() => setMode('REJETE')}>Rejetés{nRej > 0 && <span className="bdg">{nRej}</span>}</button>
        </div>
        <input
          value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher — nom, tag, rôle, motif…"
          style={{ border: '1px solid var(--line2)', borderRadius: 11, background: 'var(--card)', padding: '10px 14px', fontFamily: 'inherit', fontSize: 12.5, color: 'var(--ink)', outline: 'none', minWidth: 240 }}
        />
        <span className="rec-hint">
          {mode === 'VIVIER'
            ? 'Sortis du pipeline sans rejet — repêchables tant que la demande est ouverte.'
            : 'Rejets avec motif fermé — consultables, conservation 24 mois (loi 18-07).'}
        </span>
      </div>
      <div className="rec-vgrid">
        {list.map((a) => {
          const r = reqOf.get(a.requisitionId);
          const ent = r ? ENTITIES.find((e) => e.id === r.entityId) : undefined;
          const canRestore = mode === 'VIVIER' && canEdit && r && ['ouverte', 'validee'].includes(r.status);
          return (
            <div key={a.id} className="rec-vcard">
              <div className="hd">
                <RecAv app={a} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 800, letterSpacing: '-.01em' }}>{a.candidate.firstName} {a.candidate.lastName}</div>
                  <div style={{ fontSize: 10.5, color: 'var(--ink3)', fontWeight: 600 }}>
                    {r?.roleLabel ?? '—'} {ent && <RecEnt id={ent.id} name={ent.name} color={ent.color} />}
                  </div>
                </div>
                {a.rejectMotif
                  ? <span className="rec-stpill bad">{a.rejectMotif}</span>
                  : <span className="rec-stpill mute">{recStageOf(a.stage).lbl}</span>}
              </div>
              {a.candidate.tags.length > 0 && (
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 10 }}>
                  {a.candidate.tags.map((t) => <span key={t} className="rec-tag">{t}</span>)}
                </div>
              )}
              <div className="why">
                Sorti le {a.closedAt ? a.closedAt.split('-').reverse().join('/') : '—'}{a.closedBy ? ` par ${a.closedBy}` : ''}
                {a.rejectNote ? ` — « ${a.rejectNote} »` : ''}
              </div>
              <div className="rec-actions" style={{ marginTop: 12 }}>
                <button className="rec-ghost sm" onClick={() => onOpen(a.id)}>Ouvrir la fiche</button>
                {canRestore && (
                  <button className="rec-go sm" disabled={busy} onClick={() => onRestore(a.id)}>Repêcher → Nouvelle</button>
                )}
              </div>
            </div>
          );
        })}
        {!list.length && (
          <div className="rec-emptybox big">
            {mode === 'VIVIER' ? '♻️ Vivier vide — les sorties « Vivier » du pipeline arrivent ici.' : '📭 Aucun rejet dans ce périmètre.'}
          </div>
        )}
      </div>
    </div>
  );
}
